// ═══════════════════════════════════════════════════════════
// 搜索历史 + 观看历史
// ═══════════════════════════════════════════════════════════

import { storage } from '../lib/storage.js';
import { LIMITS, CACHE_TTL } from '../config/constants.js';
import { esc, timeAgo } from '../lib/utils.js';
import { getSiteName } from '../config/sites.js';
import { showToast } from '../ui/toast.js';

// ── 搜索历史 ──────────────────────────────────────────────

export function loadSearchHistory() {
    const history = storage.searchHistory;
    return Array.isArray(history) ? history.map(item => {
        if (typeof item === 'string') return { text: item, timestamp: 0 };
        return item;
    }).filter(item => item && item.text) : [];
}

export function saveSearchHistory(query) {
    if (!query || !query.trim()) return;
    query = query.trim().substring(0, 50).replace(/</g, '&lt;').replace(/>/g, '&gt;');

    let history = loadSearchHistory();
    const now = Date.now();

    // 清理过期记录
    history = history.filter(item =>
        item.timestamp && (now - item.timestamp < CACHE_TTL.SEARCH_HISTORY)
    );

    // 去重
    history = history.filter(item => item.text !== query);

    // 新记录开头
    history.unshift({ text: query, timestamp: now });

    if (history.length > LIMITS.SEARCH_HISTORY) {
        history = history.slice(0, LIMITS.SEARCH_HISTORY);
    }

    storage.searchHistory = history;
    renderSearchHistory();
}

export function clearSearchHistory() {
    storage.clearSearchHistory();
    renderSearchHistory();
    showToast('搜索历史已清除', 'success');
}

export function renderSearchHistory() {
    const container = document.getElementById('recentSearches');
    if (!container) return;

    const history = loadSearchHistory();

    if (history.length === 0) {
        container.innerHTML = '';
        return;
    }

    container.innerHTML = '<div class="text-gray-500 w-full mb-2">最近搜索:</div>';

    history.forEach(item => {
        const tag = document.createElement('button');
        tag.className = 'search-tag';
        tag.textContent = item.text;
        if (item.timestamp) {
            tag.title = `搜索于: ${new Date(item.timestamp).toLocaleString()}`;
        }
        tag.addEventListener('click', () => {
            const input = document.getElementById('searchInput');
            if (input) input.value = item.text;
            window.search();
        });
        container.appendChild(tag);
    });
}

// ── 观看历史 ──────────────────────────────────────────────

export function loadWatchHistory() {
    return storage.watchHistory;
}

export function saveWatchHistory(vodName, episodeIndex) {
    const info = window.getCurrentVideoInfo ? window.getCurrentVideoInfo() : null;
    if (!info) return;

    let history = loadWatchHistory();
    const eps = window.getCurrentEpisodes ? window.getCurrentEpisodes() : [];
    const reversed = false;

    const totalEps = eps.length;
    let epLabel;
    if (totalEps <= 1) {
        epLabel = '正片';
    } else {
        const displayIdx = reversed ? totalEps - 1 - episodeIndex : episodeIndex;
        epLabel = `第${displayIdx + 1}集`;
    }

    // 去重
    history = history.filter(h => !(h.vod_id === info.vod_id && h.source === info.source));

    history.unshift({
        vod_id: info.vod_id,
        title: vodName || info.title,
        episodeIndex,
        episodeLabel: epLabel,
        source: info.source,
        poster: info.poster,
        type: info.type,
        year: info.year,
        timestamp: Date.now(),
    });

    if (history.length > LIMITS.WATCH_HISTORY) {
        history = history.slice(0, LIMITS.WATCH_HISTORY);
    }

    storage.watchHistory = history;
    renderWatchHistory();
}

export function clearWatchHistory() {
    storage.clearWatchHistory();
    renderWatchHistory();
    showToast('观看历史已清除', 'success');
}

export function renderWatchHistory() {
    const container = document.getElementById('watchHistory');
    const section = document.getElementById('watchHistorySection');
    if (!container || !section) return;

    const history = loadWatchHistory();

    if (history.length === 0) {
        container.innerHTML = '<div style="color:var(--text-muted);font-size:0.85rem;padding:12px 0">暂无观看记录，搜索并播放视频后会自动出现在这里</div>';
        section.classList.remove('hidden');
        return;
    }

    section.classList.remove('hidden');

    container.innerHTML = history.slice(0, LIMITS.SHOW_WATCH_HISTORY || 8).map(h => {
        const sourceName = getSiteName(h.source);
        return `
            <div class="history-card" tabindex="0" data-vod-id="${esc(h.vod_id)}" data-vod-name="${esc(h.title).replace(/"/g, '&quot;')}">
                <div class="poster">
                    ${h.poster
                        ? `<img src="${esc(h.poster)}" alt="" loading="lazy" referrerpolicy="no-referrer"
                             onerror="this.outerHTML='<div class=&quot;placeholder&quot;>🎬</div>'" />`
                        : '<div class="placeholder">🎬</div>'
                    }
                </div>
                <div class="card-body">
                    <div class="card-title">${esc(h.title)}</div>
                    <div class="card-meta">
                        <span class="ep-badge">${esc(h.episodeLabel)}</span>
                        <span>${esc(sourceName)}</span>
                        <span>${timeAgo(h.timestamp)}</span>
                    </div>
                </div>
            </div>`;
    }).join('');

    // 绑定点击
    container.querySelectorAll('.history-card').forEach(card => {
        card.addEventListener('click', () => {
            const vodId = card.dataset.vodId;
            const vodName = card.dataset.vodName;
            if (typeof window.showVideoDetails === 'function') {
                window.showVideoDetails(vodId, vodName);
            }
        });
    });
}

// 初始化
export function initHistory() {
    renderSearchHistory();
    renderWatchHistory();
}

// 全局暴露
window.addSearchHistory = saveSearchHistory;
window.saveWatchHistoryEntry = saveWatchHistory;
window.clearSearchHistory = clearSearchHistory;
window.clearWatchHistory = clearWatchHistory;
